import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import RatingStar from './private/RatingStar';
import Form from './private/Form';
import { isTouchDevice } from './private/helpers';

import { useNotification, THEMES } from '@mos-cat/notificator';

import '@mos-utils/css/lib/typography/Link.css';
import '@mos-utils/css/lib/typography/Button.css';

import './Feedback.pcss';
import {
  MAX_SUBMIT_COUNT,
  ratingNumbers,
  ratingTexts,
  regex,
} from './constants';

const Feedback = ({ onRatingStarClick, onClickFormSubmitButton, title }) => {
  const [state, setState] = useState({
    formVisible: false,
    formSubmitted: false,
    formError: false,
    formCommentary: '',
    rating: null,
    ratingChangeCounter: 0,
    ratingHighlighted: null,
  });

  const {
    formSubmitted,
    formVisible,
    formCommentary,
    formError,
    rating,
    ratingChangeCounter,
    ratingHighlighted,
  } = state;

  const ratingChangeDisabled = ratingChangeCounter >= MAX_SUBMIT_COUNT;

  const showFormButtonShown = !formSubmitted && rating && !formVisible;
  const resetFeedbackButtonShown = formSubmitted && !ratingChangeDisabled;
  const actionButtonShown = showFormButtonShown || resetFeedbackButtonShown;

  const ratingTextClassNames = cn('MosFeedback__rating-text', {
    'MosFeedback__rating-text_hidden':
      (!ratingHighlighted && !isTouchDevice()) || ratingChangeDisabled,
  });

  const thankMessageText = formSubmitted
    ? 'Спасибо за вашу оценку и комментарий!'
    : 'Спасибо за вашу оценку!';

  const currentRatingText = ratingTexts[(ratingHighlighted || rating) - 1];

  const handleClickRatingStar = (newRating) => {
    if (!ratingChangeDisabled) {
      onRatingStarClick({ commentary: formCommentary, rating: newRating });

      setState({
        ...state,
        rating: newRating,
        ratingChangeCounter: ratingChangeCounter + 1,
        formVisible: newRating < 4 || formVisible,
      });
    }
  };

  const handleMouseEnterRatingStar = (newRatingHighlighted) => {
    setState({
      ...state,
      ratingHighlighted: newRatingHighlighted,
    });
  };

  const handleMouseLeaveRatingStar = () => {
    setState({
      ...state,
      ratingHighlighted: null,
    });
  };

  const handleClickShowFormButton = () =>
    setState({
      ...state,
      formVisible: true,
    });

  const handleClickResetFeedbackButton = () =>
    setState({
      ...state,
      rating: null,
      formCommentary: '',
      formVisible: false,
      formSubmitted: false,
    });

  const handleActionButtonClick = (event) => {
    if (showFormButtonShown) {
      handleClickShowFormButton(event);
    }
    if (resetFeedbackButtonShown) {
      handleClickResetFeedbackButton(event);
    }
  };

  const handleChangeFormCommentary = ({ target: { value } }) =>
    setState({
      ...state,
      formCommentary: value.replace(regex, ''),
      formError: false,
    });

  const { addNotification } = useNotification();

  const handleClickSubmitFormButton = () => {
    if (!formCommentary) {
      setState({
        ...state,
        formError: true,
      });

      return;
    }

    if (onClickFormSubmitButton({ commentary: formCommentary, rating })) {
      setState({
        ...state,
        formSubmitted: true,
      });

      addNotification({
        theme: THEMES.SUCCESS,
        content: () => `Комментарий отправлен`,
        delay: 5000,
        onExpire: ({ removeNotification }) => removeNotification(),
      });
    }
  };

  const handleClickCancelFormButton = () =>
    setState({
      ...state,
      formVisible: false,
      formCommentary: '',
      formError: false,
    });

  return (
    <div className="MosFeedback">
      <div className="MosFeedback__title">{title}</div>
      <div className="MosFeedback__rating">
        {ratingNumbers.map((value) => (
          <RatingStar
            value={value}
            key={value}
            onClick={handleClickRatingStar}
            onMouseEnter={handleMouseEnterRatingStar}
            onMouseLeave={handleMouseLeaveRatingStar}
            ratingHighlighted={ratingHighlighted}
            rating={rating}
            disabled={ratingChangeDisabled}
            className="MosFeedback__star"
          />
        ))}
      </div>
      <div className={ratingTextClassNames}>{currentRatingText}</div>
      {rating && (
        <div className="MosFeedback__thanks-message">{thankMessageText}</div>
      )}
      {actionButtonShown && (
        <button
          className="mos-link MosFeedback__action"
          onClick={handleActionButtonClick}
        >
          <>
            {showFormButtonShown && 'Добавить комментарий'}
            {resetFeedbackButtonShown && ' Изменить оценку и комментарий'}
          </>
        </button>
      )}
      <Form
        commentary={formCommentary}
        error={formError}
        visible={!formSubmitted && formVisible}
        onChange={handleChangeFormCommentary}
        onCancel={handleClickCancelFormButton}
        onSubmit={handleClickSubmitFormButton}
        className="MosFeedback__form"
      />
    </div>
  );
};

Feedback.propTypes = {
  onRatingStarClick: PropTypes.func.isRequired,
  onClickFormSubmitButton: PropTypes.func.isRequired,
  title: PropTypes.string,
};

Feedback.defaultProps = {
  title: 'Оцените удобство сервиса',
};

export default Feedback;
